// =============================================================================
// Simple Store - V1.5 API wrapper over V2 schema
// Provides a simplified interface for journalist writing tool
// =============================================================================

import { supabase } from './supabaseClient';
import {
  LanguageCode,
  NinjsPayload,
  generateStoryGuid,
  generateContentGuid,
} from '../types';

// =============================================================================
// SIMPLE STORY TYPE - Flattened view for V1.5
// =============================================================================

export interface SimpleStory {
  id: string;
  // From stories table (hub)
  storyId: string;
  slug: string;
  // From content_items table (spoke)
  contentItemId: string;
  language: LanguageCode;
  workflowStatus: string;
  // From content_versions table (current version)
  versionId?: string;
  versionNumber: number;
  headline: string;
  deck: string;  // summary
  body: string;
  // Metadata
  createdAt: string;
  updatedAt: string;
  authorId?: string;
  authorName?: string;
}

// =============================================================================
// CREATE - Start a new story
// =============================================================================

export async function createSimpleStory(data: {
  headline: string;
  deck: string;
  body: string;
  language: LanguageCode;
}): Promise<SimpleStory | null> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) {
    console.error('createSimpleStory: No authenticated user');
    return null;
  }

  // Generate a slug from headline (simplified)
  const slug = data.headline
    .toUpperCase()
    .replace(/[^A-Z0-9\s]/g, '')
    .replace(/\s+/g, '-')
    .slice(0, 50) + '-' + Date.now().toString(36).toUpperCase();

  const storyGuid = generateStoryGuid(slug);

  try {
    // 1. Create story (hub)
    const { data: story, error: storyError } = await supabase
      .from('stories')
      .insert({
        guid: storyGuid,
        slug,
        canonical_language: data.language,
        urgency: 5,
        is_breaking: false,
        subjects: [],
        created_by: user.id,
      })
      .select()
      .single();

    if (storyError || !story) {
      console.error('Error creating story:', storyError);
      return null;
    }

    // 2. Create content_item (spoke)
    const contentGuid = generateContentGuid(slug, data.language, 'web');
    const { data: contentItem, error: contentError } = await supabase
      .from('content_items')
      .insert({
        guid: contentGuid,
        story_id: story.id,
        language: data.language,
        item_type: 'text',
        platform: 'web',  // Default to web for simple stories
        is_canonical: true,
        workflow_status: 'draft',
        post_publication_review_required: false,
        created_by: user.id,
      })
      .select()
      .single();

    if (contentError || !contentItem) {
      console.error('Error creating content item:', contentError);
      return null;
    }

    // 3. Create initial content_version
    const ninjsPayload: NinjsPayload = {
      headline: data.headline,
      body_text: data.body,
      description_text: data.deck,
      language: data.language,
    };

    const { data: version, error: versionError } = await supabase
      .from('content_versions')
      .insert({
        content_item_id: contentItem.id,
        version_number: 1,
        version_type: 'initial',
        headline: data.headline,
        summary: data.deck,
        body_text: data.body,
        body_ninjs: ninjsPayload,
        is_auto_save: false,
        created_by: user.id,
      })
      .select()
      .single();

    if (versionError || !version) {
      console.error('Error creating content version:', versionError);
      return null;
    }

    // 4. Update content_item with current_version_id
    await supabase
      .from('content_items')
      .update({ current_version_id: version.id })
      .eq('id', contentItem.id);

    // Log action
    await supabase.from('audit_logs').insert({
      user_id: user.id,
      action: 'create_simple_story',
      entity_type: 'story',
      entity_id: story.id,
    });

    return {
      id: contentItem.id,  // Use content_item id as primary identifier
      storyId: story.id,
      slug: story.slug,
      contentItemId: contentItem.id,
      language: data.language,
      workflowStatus: 'draft',
      versionId: version.id,
      versionNumber: 1,
      headline: data.headline,
      deck: data.deck,
      body: data.body,
      createdAt: story.created_at,
      updatedAt: story.updated_at,
      authorId: user.id,
    };
  } catch (error) {
    console.error('createSimpleStory failed:', error);
    return null;
  }
}

// =============================================================================
// READ - Get stories for current user
// =============================================================================

export async function getSimpleStories(options?: {
  limit?: number;
  language?: LanguageCode;
}): Promise<SimpleStory[]> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return [];

  try {
    let query = supabase
      .from('content_items')
      .select(`
        id,
        story_id,
        language,
        workflow_status,
        current_version_id,
        created_at,
        updated_at,
        created_by,
        story:stories!inner(id, slug, created_at, updated_at, archived_at),
        current_version:content_versions!fk_current_version(
          id,
          version_number,
          headline,
          summary,
          body_text
        ),
        author:profiles!content_items_created_by_fkey(full_name)
      `)
      .eq('created_by', user.id)
      .eq('platform', 'web')  // Only web content for simple view
      .is('story.archived_at', null)  // Exclude archived
      .order('updated_at', { ascending: false });

    if (options?.language) {
      query = query.eq('language', options.language);
    }

    if (options?.limit) {
      query = query.limit(options.limit);
    }

    const { data, error } = await query;

    if (error) {
      console.error('Error fetching simple stories:', error);
      return [];
    }

    return (data || []).map((item: any) => ({
      id: item.id,
      storyId: item.story_id,
      slug: item.story?.slug || '',
      contentItemId: item.id,
      language: item.language,
      workflowStatus: item.workflow_status,
      versionId: item.current_version?.id,
      versionNumber: item.current_version?.version_number || 1,
      headline: item.current_version?.headline || '',
      deck: item.current_version?.summary || '',
      body: item.current_version?.body_text || '',
      createdAt: item.created_at,
      updatedAt: item.updated_at,
      authorId: item.created_by,
      authorName: item.author?.full_name,
    }));
  } catch (error) {
    console.error('getSimpleStories failed:', error);
    return [];
  }
}

// =============================================================================
// READ - Get single story
// =============================================================================

export async function getSimpleStory(contentItemId: string): Promise<SimpleStory | null> {
  try {
    const { data, error } = await supabase
      .from('content_items')
      .select(`
        id,
        story_id,
        language,
        workflow_status,
        current_version_id,
        created_at,
        updated_at,
        created_by,
        story:stories!inner(id, slug, created_at, updated_at),
        current_version:content_versions!fk_current_version(
          id,
          version_number,
          headline,
          summary,
          body_text
        ),
        author:profiles!content_items_created_by_fkey(full_name)
      `)
      .eq('id', contentItemId)
      .single();

    if (error || !data) {
      console.error('Error fetching simple story:', error);
      return null;
    }

    return {
      id: data.id,
      storyId: data.story_id,
      slug: (data.story as any)?.slug || '',
      contentItemId: data.id,
      language: data.language,
      workflowStatus: data.workflow_status,
      versionId: (data.current_version as any)?.id,
      versionNumber: (data.current_version as any)?.version_number || 1,
      headline: (data.current_version as any)?.headline || '',
      deck: (data.current_version as any)?.summary || '',
      body: (data.current_version as any)?.body_text || '',
      createdAt: data.created_at,
      updatedAt: data.updated_at,
      authorId: data.created_by,
      authorName: (data.author as any)?.full_name,
    };
  } catch (error) {
    console.error('getSimpleStory failed:', error);
    return null;
  }
}

// =============================================================================
// UPDATE - Save story content (creates new version)
// =============================================================================

export async function saveSimpleStory(
  contentItemId: string,
  data: {
    headline: string;
    deck: string;
    body: string;
  }
): Promise<SimpleStory | null> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return null;

  try {
    // Get current content item to find language
    const { data: contentItem } = await supabase
      .from('content_items')
      .select('language')
      .eq('id', contentItemId)
      .single();

    if (!contentItem) {
      console.error('Content item not found');
      return null;
    }

    // Get next version number
    const { data: nextNum } = await supabase
      .rpc('get_next_version_number', { p_content_item_id: contentItemId });

    // Create new version
    const ninjsPayload: NinjsPayload = {
      headline: data.headline,
      body_text: data.body,
      description_text: data.deck,
      language: contentItem.language,
    };

    const { data: version, error: versionError } = await supabase
      .from('content_versions')
      .insert({
        content_item_id: contentItemId,
        version_number: nextNum || 1,
        version_type: 'update',
        headline: data.headline,
        summary: data.deck,
        body_text: data.body,
        body_ninjs: ninjsPayload,
        is_auto_save: false,
        created_by: user.id,
      })
      .select()
      .single();

    if (versionError || !version) {
      console.error('Error creating version:', versionError);
      return null;
    }

    // Update content_item's current_version_id
    await supabase
      .from('content_items')
      .update({ current_version_id: version.id })
      .eq('id', contentItemId);

    // Return updated story
    return getSimpleStory(contentItemId);
  } catch (error) {
    console.error('saveSimpleStory failed:', error);
    return null;
  }
}

// =============================================================================
// AI REVIEW - Update workflow status to ai_review
// =============================================================================

export async function setAiReviewStatus(contentItemId: string): Promise<boolean> {
  try {
    const { error } = await supabase
      .from('content_items')
      .update({ workflow_status: 'ai_review' })
      .eq('id', contentItemId);

    if (error) {
      console.error('Error setting AI review status:', error);
      return false;
    }

    return true;
  } catch (error) {
    console.error('setAiReviewStatus failed:', error);
    return false;
  }
}

// =============================================================================
// BACK TO DRAFT - Return to draft status after AI review
// =============================================================================

export async function setDraftStatus(contentItemId: string): Promise<boolean> {
  try {
    const { error } = await supabase
      .from('content_items')
      .update({ workflow_status: 'draft' })
      .eq('id', contentItemId);

    if (error) {
      console.error('Error setting draft status:', error);
      return false;
    }

    return true;
  } catch (error) {
    console.error('setDraftStatus failed:', error);
    return false;
  }
}

// =============================================================================
// DELETE - Archive a story (soft delete)
// =============================================================================

export async function archiveSimpleStory(contentItemId: string): Promise<boolean> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return false;

  try {
    // Get story_id first
    const { data: contentItem } = await supabase
      .from('content_items')
      .select('story_id')
      .eq('id', contentItemId)
      .single();

    if (!contentItem) return false;

    // Archive the story (hub)
    const { error } = await supabase
      .from('stories')
      .update({ archived_at: new Date().toISOString() })
      .eq('id', contentItem.story_id);

    if (error) {
      console.error('Error archiving story:', error);
      return false;
    }

    // Log action
    await supabase.from('audit_logs').insert({
      user_id: user.id,
      action: 'archive_simple_story',
      entity_type: 'story',
      entity_id: contentItem.story_id,
    });

    return true;
  } catch (error) {
    console.error('archiveSimpleStory failed:', error);
    return false;
  }
}

// =============================================================================
// VERSION HISTORY - Get all versions for a story
// =============================================================================

export interface SimpleVersion {
  id: string;
  versionNumber: number;
  headline: string;
  deck: string;
  body: string;
  createdAt: string;
  createdBy?: string;
}

export async function getVersionHistory(contentItemId: string): Promise<SimpleVersion[]> {
  try {
    const { data, error } = await supabase
      .from('content_versions')
      .select('id, version_number, headline, summary, body_text, created_at, created_by')
      .eq('content_item_id', contentItemId)
      .order('version_number', { ascending: false });

    if (error) {
      console.error('Error fetching version history:', error);
      return [];
    }

    return (data || []).map((v: any) => ({
      id: v.id,
      versionNumber: v.version_number,
      headline: v.headline || '',
      deck: v.summary || '',
      body: v.body_text || '',
      createdAt: v.created_at,
      createdBy: v.created_by,
    }));
  } catch (error) {
    console.error('getVersionHistory failed:', error);
    return [];
  }
}

// =============================================================================
// RESTORE VERSION - Restore a previous version
// =============================================================================

export async function restoreVersion(
  contentItemId: string,
  versionId: string
): Promise<SimpleStory | null> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return null;

  try {
    // Get the version to restore
    const { data: oldVersion } = await supabase
      .from('content_versions')
      .select('headline, summary, body_text')
      .eq('id', versionId)
      .single();

    if (!oldVersion) {
      console.error('Version not found');
      return null;
    }

    // Save as new version (don't overwrite history)
    return saveSimpleStory(contentItemId, {
      headline: oldVersion.headline || '',
      deck: oldVersion.summary || '',
      body: oldVersion.body_text || '',
    });
  } catch (error) {
    console.error('restoreVersion failed:', error);
    return null;
  }
}
